import styled from "styled-components";
import {
  colors,
  spacing,
  radius,
  stroke,
  textStyles,
  toCSS
} from "design-system/foundation";
import { breakpoints } from "styles";
import Image from "next/image";

// === Card Layout ===

export const CardContainer = styled.div`
  &,
  & *,
  & *::before,
  & *::after {
    box-sizing: border-box;
  }

  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  gap: ${spacing["4xl"]};

  @media ${breakpoints.tablet} {
    flex-direction: row;
    width: 1024px;
    padding: ${spacing["4xl"]};
    border-radius: ${radius.xl};
    background-color: ${colors.bg.base.default};
  }
`;

// === Card Left ===

export const CardLeft = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing["2xl"]} ${spacing["2xl"]} ${spacing.none};

  @media ${breakpoints.tablet} {
    width: 400px;
    padding: ${spacing.none};
    justify-content: space-between;
  }
`;

export const PracticeInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing["2xl"]};
  gap: ${spacing["2xl"]};
  border: ${stroke.md} solid ${colors.border.state.default};
  border-radius: ${radius.lg};
`;

export const InfoTopic = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.md};
`;

export const TopicImage = styled(Image)`
  width: 40px;
  height: 40px;
  padding: ${spacing.xs};
`;

export const TopicTitle = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.caption.caption5)};
  color: ${colors.text.base.primary};
`;

export const InfoProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  border-radius: ${radius["5xl"]};
  background: ${colors.bg.brand.secondarySoft};
`;

export const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress * 100}%;
  border-radius: ${radius["5xl"]};
  background: ${colors.bg.brand.primaryBold};
`;

export const ProgressStatus = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const StatusCount = styled.p`
  margin: ${spacing.none};
  color: ${colors.text.base.secondary};
  ${toCSS(textStyles.body.body5Bold)};
`;

export type StatusStateVariant = "ongoing" | "correct" | "wrong";

const getStatusColor = (variant: StatusStateVariant) => {
  if (variant === "correct") return colors.text.semantic.success;
  if (variant === "wrong") return colors.text.semantic.error;
  return colors.text.base.disabled;
};

export const StatusState = styled.p<{ $variant: StatusStateVariant }>`
  margin: ${spacing.none};
  color: ${({ $variant }) => getStatusColor($variant)};
  ${toCSS(textStyles.body.body5Bold)};
`;

export const ButtonGroup = styled.div<{ $mobile?: boolean }>`
  display: ${({ $mobile }) => ($mobile ? "flex" : "none")};
  flex-direction: column;
  gap: ${spacing["2xl"]};
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  padding: ${spacing["2xl"]};
  border-top: ${({ $mobile }) =>
    $mobile ? `${stroke.xs} solid ${colors.border.state.default}` : "none"};
  background-color: ${colors.bg.base.default};
  @media ${breakpoints.tablet} {
    display: ${({ $mobile }) => ($mobile ? "none" : "flex")};
    position: static;
    padding: ${spacing.none};
  }
`;

export const ActionDescription = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.body.body4Regular)};
  color: ${colors.text.base.secondary};
  text-align: center;
`;

export const Divider = styled.div`
  display: none;

  @media ${breakpoints.tablet} {
    display: block;
    width: ${stroke.md};
    height: auto;
    background-color: ${colors.border.state.default};
  }
`;

// === Card Right ===

export const CardRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing["4xl"]};
  /* Reserve room for the fixed mobile footer so the last section is not covered. */
  padding: ${spacing.none} ${spacing["2xl"]} 208px;
  flex: 1;

  @media ${breakpoints.tablet} {
    overflow-y: auto;
    padding: ${spacing.none};
  }
`;

// === Collapsible Card ===

export type CardBorderVariant = "default" | "correct" | "wrong";

const getCardBorderColor = (variant: CardBorderVariant) => {
  if (variant === "correct") return colors.border.semantic.success;
  if (variant === "wrong") return colors.border.semantic.error;
  return colors.border.state.default;
};

export const CollapsibleCardWrapper = styled.div<{
  $border?: CardBorderVariant;
  $isExpanded?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isExpanded }) => ($isExpanded ? spacing.md : spacing.none)};
  padding: ${spacing["2xl"]};
  border: ${stroke.md} solid
    ${({ $border }) => getCardBorderColor($border ?? "default")};
  border-radius: ${radius.lg};
  transition: gap 0.2s ease;
`;

export const CollapsibleContent = styled.div<{ $isExpanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? "1fr" : "0fr")};
  overflow: hidden;
  pointer-events: ${({ $isExpanded }) => ($isExpanded ? "auto" : "none")};
  transition: grid-template-rows 0.2s ease;

  > div {
    display: flex;
    flex-direction: column;
    gap: ${spacing["2xl"]};
    min-height: 0;
    overflow: hidden;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.md};
`;

export const CardChevron = styled.button<{ $isExpanded?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${spacing.none};

  .icon {
    transition: transform 0.2s ease;
    transform: ${({ $isExpanded }) =>
      $isExpanded ? "rotate(0deg)" : "rotate(180deg)"};
    display: flex;
    align-items: center;
    font-size: 24px;
    color: ${colors.icon.base.primary};
  }
`;

export const TextContentWrapper = styled.div`
  margin: ${spacing.none};
  color: ${colors.text.base.primary};
  ${toCSS(textStyles.body.body3Regular)};
`;

export const QuestionAssetList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing["2xl"]};
`;

export const QuestionAssetButton = styled.button`
  display: block;
  max-width: 100%;
  padding: ${spacing.none};
  border: none;
  background: transparent;
  cursor: pointer;
`;

export const QuestionAssetImage = styled.img`
  display: block;
  width: auto;
  max-width: 100%;
  height: auto;
  border-radius: ${radius.sm};
`;

export const AttemptImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const AttemptImageButton = styled.button`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  padding: ${spacing.none};
  border: none;
  border-radius: ${radius.sm};
  background: #000;
  cursor: pointer;
`;

// === MCQ Options ===

export const McqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

export const McqContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.md};
`;

export const McqAssetImage = styled.img`
  display: block;
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: 240px;
  border-radius: ${radius.sm};
  cursor: pointer;
`;

type McqOptionStatus = "default" | "selected" | "correct" | "incorrect";

const getMcqStatusColor = (status: McqOptionStatus) => {
  if (status === "correct") return colors.border.semantic.success;
  if (status === "incorrect") return colors.border.semantic.error;
  if (status === "selected") return colors.border.semantic.brand;
  return colors.border.state.default;
};

export const McqItem = styled.button<{
  $selected?: boolean;
  $status?: McqOptionStatus;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing["2xl"]};
  padding: ${spacing["2xl"]};
  border: ${stroke.md} solid
    ${({ $status, $selected }) =>
      getMcqStatusColor($status ?? ($selected ? "selected" : "default"))};
  border-radius: ${radius.xs};
  background: ${({ $status, $selected }) => {
    const status = $status ?? ($selected ? "selected" : "default");
    if (status === "correct") return colors.bg.success.secondarySoft;
    if (status === "incorrect") return colors.bg.error.secondarySoft;
    if (status === "selected") return colors.bg.brand.secondarySoft;
    return colors.bg.base.default;
  }};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  text-align: left;
`;

export const McqLabel = styled.span<{
  $selected?: boolean;
  $status?: McqOptionStatus;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  padding: ${spacing.md};
  ${toCSS(textStyles.caption.caption7)};
  background: ${({ $status, $selected }) => {
    const status = $status ?? ($selected ? "selected" : "default");
    if (status === "correct") return colors.bg.success.primaryBold;
    if (status === "incorrect") return colors.bg.error.primaryBold;
    return status === "selected"
      ? colors.bg.brand.primaryBold
      : colors.bg.base.disabled;
  }};
  color: ${({ $status, $selected }) => {
    const status = $status ?? ($selected ? "selected" : "default");
    return status === "default"
      ? colors.text.base.secondary
      : colors.text.base.invert;
  }};
`;

export const McqText = styled.span<{
  $selected?: boolean;
  $status?: McqOptionStatus;
}>`
  flex: 1;
  ${({ $status, $selected }) => {
    const status = $status ?? ($selected ? "selected" : "default");
    return status === "default"
      ? toCSS(textStyles.body.body4Regular)
      : toCSS(textStyles.body.body4Bold);
  }};
  color: ${({ $status, $selected }) => {
    const status = $status ?? ($selected ? "selected" : "default");
    if (status === "correct") return colors.text.semantic.success;
    if (status === "incorrect") return colors.text.semantic.error;
    return status === "selected"
      ? colors.text.semantic.brand
      : colors.text.base.primary;
  }};
`;

export const McqRadio = styled.span<{
  $selected?: boolean;
  $status?: McqOptionStatus;
}>`
  width: 24px;
  height: 24px;
  padding: ${spacing["2xs"]};
  border-radius: 50%;
  flex-shrink: 0;
  border: ${stroke.lg} solid
    ${({ $status, $selected }) => {
      const status = $status ?? ($selected ? "selected" : "default");
      return status === "correct"
        ? colors.border.semantic.success
        : status === "incorrect"
          ? colors.border.semantic.error
          : status === "selected"
            ? colors.border.semantic.brand
            : colors.border.state.disable;
    }};
  background: ${({ $status, $selected }) => {
    const status = $status ?? ($selected ? "selected" : "default");
    return status === "correct"
      ? colors.bg.success.primaryBold
      : status === "incorrect"
        ? colors.bg.error.primaryBold
        : status === "selected"
          ? colors.bg.brand.primaryBold
          : "transparent";
  }};
  box-shadow: ${({ $selected }) =>
    $selected ? `inset 0 0 0 3px ${colors.bg.base.default}` : "none"};
`;
